import { numberToNumberInputString, numberInputStringToNumber } from 'common/formatting/numberInput';
import { isDefined } from 'common/utils';

import { RequiredSelection, OptionBindingFormValues } from './types';
import { Option, OptionBinding, OptionBindingAttrs } from '../../types';
import { updatePrimaryLanguageTranslation } from '../../utils';

export const DEFAULT_OPTION: RequiredSelection = { option: '', choice: '' };

export const findOption = (otherOptions: Option[], choiceId: string): Option | undefined => {
  return otherOptions.find(opt => opt.values.some(optionValue => optionValue.id === choiceId || false));
};

const findOptionChoiceOption =
  (otherOptions: Option[]) =>
  (choiceId: string): RequiredSelection => {
    const option = findOption(otherOptions, choiceId);
    if (!option) {
      return DEFAULT_OPTION;
    }

    return { option: (option.id && option.id) || '', choice: choiceId };
  };

export const optionBindingToOptionBindingFormValues = (
  primaryLanguage: Language,
  optionBinding: OptionBinding,
  otherOptions: Option[],
): OptionBindingFormValues => {
  const nameTranslation = (optionBinding.name ?? []).find(t => t.lang === primaryLanguage);
  return {
    externalId: optionBinding.external_id || '',
    freeSelections: optionBinding.multi_choice_config
      ? numberToNumberInputString(optionBinding.multi_choice_config.free_selections ?? 0)
      : '',
    maximumSingleSelections: optionBinding.multi_choice_config
      ? numberToNumberInputString(optionBinding.multi_choice_config.max_single_selections)
      : '',
    maximumTotalSelections: optionBinding.multi_choice_config
      ? numberToNumberInputString(optionBinding.multi_choice_config.total_selections.max)
      : '',
    minimumTotalSelections: optionBinding.multi_choice_config
      ? numberToNumberInputString(optionBinding.multi_choice_config.total_selections.min)
      : '',
    name: nameTranslation ? nameTranslation.value : '',
    requiredSelections:
      optionBinding.prerequisite_values && optionBinding.prerequisite_values.length > 0
        ? optionBinding.prerequisite_values.map(findOptionChoiceOption(otherOptions))
        : [DEFAULT_OPTION],
    useRequiredSelections: (optionBinding.prerequisite_values ?? []).length > 0,
  };
};

export const optionBindingFormValuesToUpdatePayload = (
  primaryLanguage: Language,
  optionBinding: OptionBinding,
  formValues: OptionBindingFormValues,
): OptionBinding => {
  const requiredSelections = formValues.useRequiredSelections
    ? formValues.requiredSelections.filter(({ choice }) => choice.length > 0).map(({ choice }) => choice)
    : [];

  const multiChoiceConfig = optionBinding.multi_choice_config
    ? {
        free_selections: numberInputStringToNumber(formValues.freeSelections),
        max_single_selections: numberInputStringToNumber(formValues.maximumSingleSelections),
        total_selections: {
          min: numberInputStringToNumber(formValues.minimumTotalSelections),
          max: numberInputStringToNumber(formValues.maximumTotalSelections),
        },
      }
    : undefined;

  return {
    external_id: formValues.externalId || undefined,
    id: optionBinding.id,
    multi_choice_config: multiChoiceConfig,
    name: formValues.name
      ? updatePrimaryLanguageTranslation(primaryLanguage, formValues.name, optionBinding.name)
      : undefined,
    option_id: optionBinding.option_id,
    prerequisite_values: requiredSelections ?? undefined,
  };
};

export const buildOptionBindingCreatePayload = (option: Option): OptionBindingAttrs => {
  return {
    option_id: option.id,
    prerequisite_values: undefined,
    multi_choice_config:
      option.type === 'multi_choice'
        ? {
            total_selections: {
              min: 0,
              max: Math.max(
                1,
                option.values.length,
                option.values.reduce((acc, option) => {
                  if (!option.multi_choice_config) {
                    throw new Error('Multi-choice option does not have multi_choice_config');
                  }
                  return Math.max(acc, option.multi_choice_config.selection_range.max);
                }, 0),
              ),
            },
            max_single_selections: 1,
            free_selections: 0,
          }
        : undefined,
    name: undefined,
    external_id: undefined,
  };
};

export const cleanUpInvalidPrerequisiteOptionChoices = (
  newOptionBindings: OptionBinding[],
  oldOptionBindings: OptionBinding[],
  options: Option[],
) => {
  const removedOptionBindings = oldOptionBindings.filter(ob => !newOptionBindings.some(newOb => newOb.id === ob.id));
  const removedOptionChoiceIds = removedOptionBindings
    .flatMap(ob => options.find(option => option.id === ob.option_id)?.values.map(choice => choice.id))
    .filter(isDefined);

  return newOptionBindings.map(ob => ({
    ...ob,
    prerequisite_values: (ob.prerequisite_values ?? []).filter(
      prereqChoiceId => !removedOptionChoiceIds.includes(prereqChoiceId),
    ),
  }));
};
